import { HGCollection } from "./types";
import { collectionParentIds } from "./utils";

/**
 * A collection graph expands into one node per *path*, not per collection: a
 * collection reachable from two parents is two rows, and each folds on its own.
 * The expansion is bounded only by the cycle guard, so a wide diamond graph can
 * in principle produce a great many nodes. This caps it — a truncated list is a
 * recoverable annoyance, a hung sidebar is not.
 */
const MAX_NODES = 2000;

export interface CollectionNode {
  collection: HGCollection;
  /** True when this node is here because of a title record, not a link the user made. */
  implicit: boolean;
  /**
   * Node identity: the collection ids from the root joined by "/". Unlike
   * `collection.id` this is unique across the whole forest, which is what lets
   * the same collection sit under two parents as two independent nodes.
   */
  key: string;
  children: CollectionNode[];
  /** Every node below this one, for the "+N" hint on a row that goes deeper. */
  descendantCount: number;
}

type Edge = { collection: HGCollection; implicit: boolean };

/**
 * Turns the flat collection list into a forest, following both the explicit
 * links the owner configured and the implicit ones a title record creates.
 *
 * A root is a collection with no parent of either kind *that is in this list*.
 * The visibility test is not paranoia: the default collection is filtered out
 * server-side (`get_collections`, `is_default = false`), so a collection whose
 * only edge is an implicit one to it — what promoting a record filed nowhere but
 * "Without collection" produces, and what a space-level import produces for
 * every `both` node — has a non-empty parent list and no visible parent. Without
 * the test it is neither a root nor anybody's child, and never renders at all.
 *
 * Sibling order is inherited from `collections`, which the API layer already
 * sorts alphabetically.
 */
export function buildCollectionTree(
  collections: HGCollection[],
): CollectionNode[] {
  const visibleIDs = new Set(collections.map((collection) => collection.id));

  // Indexed from each collection's own parent arrays rather than the parents'
  // child arrays, so an edge naming a collection this viewer cannot see never
  // produces a row.
  const byParent = new Map<string, Edge[]>();
  const roots: HGCollection[] = [];

  for (const collection of collections) {
    const explicit = new Set(collection.parent_ids);
    // One edge per parent, not per id: filing a collection's title record into
    // a collection it is *also* explicitly linked to names that parent in both
    // arrays, and listing the child twice under it is not what that means.
    const parentIDs = new Set(collectionParentIds(collection));
    let attached = false;

    for (const parentID of parentIDs) {
      if (!visibleIDs.has(parentID)) continue;
      const edges = byParent.get(parentID);
      // An edge that is both explicit and implicit counts as explicit.
      const edge = { collection, implicit: !explicit.has(parentID) };
      if (edges) edges.push(edge);
      else byParent.set(parentID, [edge]);
      attached = true;
    }

    if (!attached) roots.push(collection);
  }

  let budget = MAX_NODES;

  /**
   * `ancestors` is not an optimisation. Implicit edges make this graph genuinely
   * cyclic — filing collection A's title record into B while B's title record
   * sits in A is a loop neither write could have refused — and without the guard
   * this recursion would never return.
   */
  const build = (
    edge: Edge,
    parentKey: string,
    ancestors: Set<string>,
  ): CollectionNode => {
    const { collection, implicit } = edge;
    const key = parentKey ? `${parentKey}/${collection.id}` : collection.id;
    const nextAncestors = new Set(ancestors).add(collection.id);

    const children: CollectionNode[] = [];
    let descendantCount = 0;

    for (const childEdge of byParent.get(collection.id) ?? []) {
      if (nextAncestors.has(childEdge.collection.id)) continue;
      if (budget <= 0) break;
      budget -= 1;
      const child = build(childEdge, key, nextAncestors);
      children.push(child);
      descendantCount += 1 + child.descendantCount;
    }

    return { collection, implicit, key, children, descendantCount };
  };

  const forest = roots.map((collection) => {
    budget -= 1;
    return build({ collection, implicit: false }, "", new Set<string>());
  });

  // Every collection in a cycle has a visible parent, so none of them is a root
  // and — walking only downwards from the roots — none of them would render at
  // all. That is the same disappearance BUGS.md records for a collection
  // parented only to the invisible default one, reached the other way round.
  // Whatever the walk missed is promoted, which pulls the rest of its loop in
  // behind it.
  const reached = new Set<string>();
  const mark = (nodes: CollectionNode[]) => {
    for (const node of nodes) {
      reached.add(node.collection.id);
      mark(node.children);
    }
  };
  mark(forest);

  for (const collection of collections) {
    if (reached.has(collection.id)) continue;
    budget -= 1;
    const node = build({ collection, implicit: false }, "", new Set<string>());
    forest.push(node);
    mark([node]);
  }

  return forest;
}

/** One rung of the drill-down: the rows to list, and how we got here. */
export interface CollectionLevel {
  /** The collections at this level — the roots, or one node's children. */
  nodes: CollectionNode[];
  /**
   * The nodes walked through to reach it, outermost first. Can be shorter than
   * the path asked for: a stored path naming a collection since deleted or
   * re-parented stops the walk, and the level it got to is shown instead. The
   * caller drills from *this*, not from what it stored, so the stored path heals
   * on the next move.
   */
  trail: CollectionNode[];
}

/** The level `path` (collection ids, outermost first) points at. */
export function resolveLevel(
  roots: CollectionNode[],
  path: string[],
): CollectionLevel {
  let nodes = roots;
  const trail: CollectionNode[] = [];

  for (const id of path) {
    const next = nodes.find((node) => node.collection.id === id);
    if (!next) break;
    trail.push(next);
    nodes = next.children;
  }

  return { nodes, trail };
}

/**
 * The path to open so that `collectionID` is one of the rows on screen — its
 * ancestors, not the collection itself, since the level a collection is *in* is
 * the one that lists it. Empty when it is a root, or unreachable.
 *
 * The first path that reaches it wins; a collection filed under two parents has
 * more than one.
 */
export function pathToCollection(
  roots: CollectionNode[],
  collectionID: string,
): string[] {
  const search = (
    nodes: CollectionNode[],
    trail: string[],
  ): string[] | null => {
    for (const node of nodes) {
      if (node.collection.id === collectionID) return trail;
      const found = search(node.children, [...trail, node.collection.id]);
      if (found) return found;
    }
    return null;
  };

  return search(roots, []) ?? [];
}
